import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * P1.1: Unified Invite Acceptance Page
 * - Handles both buyer AND admin invites
 * - Peeks at invite to determine role
 * - Shows wallet field ONLY for buyers (optional)
 */
const InviteAcceptance = () => {
  const navigate = useNavigate();
  const { token } = useParams();
  
  // Invite state
  const [inviteData, setInviteData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [peekError, setPeekError] = useState('');
  
  // Form state
  const [name, setName] = useState('');
  const [walletAddress, setWalletAddress] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Peek at invite on load
  useEffect(() => {
    const peekInvite = async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/invites/peek?token=${token}`);
        const data = await res.json();
        
        if (!res.ok) {
          setPeekError(data.detail || 'Invalid invite token');
          setLoading(false);
          return;
        }
        
        if (data.used) {
          setPeekError('This invite has already been used');
          setLoading(false);
          return;
        }
        
        if (data.expired) {
          setPeekError('This invite has expired');
          setLoading(false);
          return;
        }
        
        setInviteData(data);
      } catch (err) {
        console.error('Error peeking invite:', err);
        setPeekError('Failed to load invite details');
      } finally {
        setLoading(false);
      }
    };
    
    if (token) {
      peekInvite();
    }
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    // Validate password
    if (!password || password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    
    setSubmitting(true);
    
    try {
      const payload = {
        token: token,
        password: password,
        name: name || undefined,
        wallet_address: walletAddress || undefined
      };
      
      const res = await fetch(`${BACKEND_URL}/api/invites/accept`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      
      const data = await res.json();
      
      if (!res.ok) {
        setError(data.detail || 'Failed to accept invite');
        setSubmitting(false);
        return;
      }
      
      // Store JWT and redirect
      localStorage.setItem('token', data.token);
      navigate(data.redirect_to || `/onboarding/${inviteData.role}`);
      
    } catch (err) {
      console.error('Error accepting invite:', err);
      setError('An error occurred. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 text-white flex items-center justify-center" data-testid="invite-loading">
        <div className="text-xl">Loading invite details...</div>
      </div>
    );
  }

  // Error state (invalid/used/expired token)
  if (peekError) {
    return (
      <div className="min-h-screen bg-gray-900 text-white flex items-center justify-center p-4" data-testid="invite-error">
        <div className="max-w-md w-full bg-gray-800 rounded-lg p-8 text-center">
          <div className="text-red-400 text-6xl mb-4">⚠</div>
          <h1 className="text-2xl font-bold text-red-400 mb-4">Invalid Invite</h1>
          <p className="text-gray-400 mb-6">{peekError}</p>
          <button 
            onClick={() => navigate('/auth')}
            className="px-6 py-2 bg-blue-600 rounded hover:bg-blue-700"
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  const isBuyer = inviteData?.role === 'buyer';
  const isAdmin = inviteData?.role === 'admin';
  const roleLabel = isAdmin ? 'Admin' : 'Holder';

  return (
    <div className="min-h-screen bg-gray-900 text-white flex items-center justify-center p-4" data-testid="invite-acceptance-page">
      <div className="max-w-md w-full bg-gray-800 rounded-lg p-8">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-blue-400 mb-2">You're Invited!</h1>
          <p className="text-gray-400">
            Accept your invitation to become {isAdmin ? 'an' : 'a'} <span className="text-blue-300 font-semibold">{roleLabel}</span>
          </p>
          <p className="text-sm text-gray-500 mt-2">
            Invite for: {inviteData?.email}
          </p>
        </div>

        {/* Benefits */}
        <div className="bg-gray-700 p-6 rounded-lg mb-6">
          <h2 className="text-lg font-bold mb-3">What You'll Get:</h2>
          {isBuyer ? (
            <ul className="space-y-2 text-sm text-gray-300">
              <li>✓ Access to exclusive NFT portfolios</li>
              <li>✓ Automatic payout distributions</li>
              <li>✓ Governance voting rights</li>
              <li>✓ Portfolio utilities and perks</li>
            </ul>
          ) : (
            <ul className="space-y-2 text-sm text-gray-300">
              <li>✓ Full platform administration access</li>
              <li>✓ User management capabilities</li>
              <li>✓ System configuration controls</li>
              <li>✓ Analytics and reporting dashboard</li>
            </ul>
          )}
        </div>

        {/* Error display */}
        {error && (
          <div className="mb-4 p-3 bg-red-900/50 border border-red-600 rounded text-red-300 text-sm" data-testid="error-message">
            {error}
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm text-gray-400 mb-2">Your Name (optional)</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full p-3 bg-gray-700 rounded border border-gray-600 focus:border-blue-500 outline-none"
              placeholder="John Doe"
              data-testid="name-input"
            />
          </div>

          {/* Wallet field - ONLY for buyers, OPTIONAL */}
          {isBuyer && (
            <div>
              <label className="block text-sm text-gray-400 mb-2">
                Wallet Address <span className="text-gray-500">(optional)</span>
              </label>
              <input
                type="text"
                value={walletAddress}
                onChange={(e) => setWalletAddress(e.target.value)}
                className="w-full p-3 bg-gray-700 rounded border border-gray-600 focus:border-blue-500 outline-none"
                placeholder="0x..."
                data-testid="wallet-input"
              />
              <p className="text-xs text-gray-500 mt-1">
                You can add this later. Used for receiving NFTs and payouts.
              </p>
            </div>
          )}

          <div>
            <label className="block text-sm text-gray-400 mb-2">Create Password *</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full p-3 bg-gray-700 rounded border border-gray-600 focus:border-blue-500 outline-none"
              placeholder="Minimum 8 characters"
              required
              minLength={8}
              data-testid="password-input"
            />
          </div>

          <div>
            <label className="block text-sm text-gray-400 mb-2">Confirm Password *</label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="w-full p-3 bg-gray-700 rounded border border-gray-600 focus:border-blue-500 outline-none"
              placeholder="Re-enter password"
              required
              minLength={8}
              data-testid="confirm-password-input"
            />
          </div>

          <button
            type="submit"
            disabled={submitting}
            className="w-full py-3 bg-green-600 rounded hover:bg-green-700 disabled:bg-gray-600 disabled:cursor-not-allowed font-semibold"
            data-testid="accept-invite-btn"
          >
            {submitting ? 'Creating Account...' : `Accept Invite & Become ${roleLabel}`}
          </button>
        </form>

        <div className="mt-6 p-4 bg-yellow-900/30 border border-yellow-600 rounded">
          <p className="text-yellow-400 text-xs">
            <strong>Note:</strong> By accepting, you agree to the platform terms and conditions.
          </p>
        </div>
      </div>
    </div>
  );
};

export default InviteAcceptance;
